import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { AlertTriangle, X, Mail, Pencil, Camera, User, Download, Shield, FileJson, Loader2, Palette } from 'lucide-react'
import { ThemeSwitcher } from '@/components/ThemeSwitcher'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useDropsQuery } from '@/data/drops'
import { syncCurrentUserEmail } from '@/data/users'
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  useNotificationPreferencesQuery,
  useUpsertNotificationPreferencesMutation,
  type DigestMode,
} from '@/data/notificationPreferences'

function clerkErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'errors' in err) {
    const errors = (err as { errors?: Array<{ longMessage?: string; message?: string }> }).errors
    if (errors && errors[0]) {
      return errors[0].longMessage ?? errors[0].message ?? fallback
    }
  }
  return err instanceof Error ? err.message : fallback
}

export default function SettingsPage() {
  const { hasPermission } = useAuth()
  const canDeleteAccount = hasPermission('delete_account')

  return (
    <div className="space-y-8 max-w-2xl">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Profile Section */}
      <ProfileSection />

      {/* Notification Settings */}
      <NotificationsSection />

      {/* Appearance Section */}
      <div className="bg-card rounded-lg border p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Appearance</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose your preferred theme. System default will follow your operating system settings (defaults to dark).
        </p>
        <ThemeSwitcher />
      </div>

      {/* Billing Section */}
      <div className="bg-card rounded-lg border p-6 space-y-6">
        <h2 className="text-lg font-semibold">Billing</h2>

        <div>
          <p className="font-medium">Current Plan: Free</p>
          <p className="text-sm text-muted-foreground mt-1">
            2 drops, 50MB per contributor
          </p>
        </div>

        <Button variant="outline">Upgrade to Pro</Button>
      </div>

      {/* Privacy Section */}
      <PrivacySection />

      {/* Danger Zone - Only show if user has permission */}
      {canDeleteAccount && (
        <DangerZone />
      )}
    </div>
  )
}

function ProfileSection() {
  const { user, updateUserName, updateUserAvatar } = useAuth()
  const { toast } = useToast()
  const [name, setName] = useState(user?.name || '')
  const [isSaving, setIsSaving] = useState(false)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Name cannot be empty.',
        variant: 'destructive',
      })
      return
    }

    setIsSaving(true)

    try {
      // Update the user name in the auth context (persists to localStorage)
      await updateUserName(name.trim())

      toast({
        title: 'Profile updated',
        description: 'Your profile has been saved successfully.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        variant: 'destructive',
      })
    }

    setIsSaving(false)
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file.',
        variant: 'destructive',
      })
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image under 5MB.',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateUserAvatar(file)
      toast({
        title: 'Avatar updated',
        description: 'Your profile picture has been updated.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update avatar. Please try again.',
        variant: 'destructive',
      })
    }

    e.target.value = ''
  }

  const handleRemoveAvatar = async () => {
    try {
      await updateUserAvatar(null)
      toast({
        title: 'Avatar removed',
        description: 'Your profile picture has been removed.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove avatar. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <div className="bg-card rounded-lg border p-6 space-y-6">
        <h2 className="text-lg font-semibold">Profile</h2>

        <form onSubmit={handleSaveProfile} className="space-y-6">
          {/* Avatar Section */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Profile Picture
            </label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div
                  className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-medium cursor-pointer overflow-hidden border-2 border-muted hover:border-primary transition-colors"
                  onClick={handleAvatarClick}
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{user?.name?.charAt(0).toUpperCase() || <User className="h-8 w-8" />}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAvatarClick}
                >
                  Change Avatar
                </Button>
                {user?.avatarUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={handleRemoveAvatar}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Recommended: Square image, at least 200x200px
            </p>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Name
            </label>
            <input
              id="name"
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Email
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 px-3 py-2 border rounded-md bg-muted/50 text-muted-foreground">
                {user?.email || 'No email set'}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsEmailModalOpen(true)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit Email
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Email changes require password verification
            </p>
          </div>

          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </form>
      </div>

      {isEmailModalOpen && (
        <ChangeEmailModal onClose={() => setIsEmailModalOpen(false)} />
      )}
    </>
  )
}

function NotificationsSection() {
  const { toast } = useToast()
  const prefsQuery = useNotificationPreferencesQuery()
  const upsertMutation = useUpsertNotificationPreferencesMutation()

  // Drive form state from the query result, falling back to defaults while
  // loading or when the user has no row yet.
  const loaded = prefsQuery.data?.preferences
  const userId = prefsQuery.data?.userId
  const initial = loaded ?? {
    email_enabled: DEFAULT_NOTIFICATION_PREFERENCES.email_enabled,
    sms_enabled: DEFAULT_NOTIFICATION_PREFERENCES.sms_enabled,
    in_app_enabled: DEFAULT_NOTIFICATION_PREFERENCES.in_app_enabled,
    digest_mode: DEFAULT_NOTIFICATION_PREFERENCES.digest_mode,
  }
  const [emailNotifications, setEmailNotifications] = useState<boolean>(initial.email_enabled)
  const [smsNotifications, setSmsNotifications] = useState<boolean>(initial.sms_enabled)
  const [digestMode, setDigestMode] = useState<DigestMode>(initial.digest_mode)
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)

  // Hydrate the form once we receive the user's row (or null) from the server.
  // Keyed by `userId` so a re-fetch with the same user doesn't clobber local edits.
  if (userId && hydratedFor !== userId) {
    setHydratedFor(userId)
    if (loaded) {
      setEmailNotifications(loaded.email_enabled)
      setSmsNotifications(loaded.sms_enabled)
      setDigestMode(loaded.digest_mode)
    }
  }

  const handleSave = async () => {
    if (!userId) {
      toast({
        variant: 'destructive',
        title: 'Profile not ready',
        description: 'Your account is still being set up. Try again in a moment.',
      })
      return
    }
    try {
      await upsertMutation.mutateAsync({
        userId,
        emailEnabled: emailNotifications,
        smsEnabled: smsNotifications,
        inAppEnabled: true,
        digestMode,
      })
      const description =
        digestMode === 'daily'
          ? 'You will receive a daily digest email with all your notifications.'
          : digestMode === 'off'
          ? 'Email digests are off; you will still receive in-app notifications.'
          : 'Your notification preferences have been updated.'
      toast({ title: 'Notifications saved', description })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to save notifications',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  const handleResetToDefaults = async () => {
    setEmailNotifications(DEFAULT_NOTIFICATION_PREFERENCES.email_enabled)
    setSmsNotifications(DEFAULT_NOTIFICATION_PREFERENCES.sms_enabled)
    setDigestMode(DEFAULT_NOTIFICATION_PREFERENCES.digest_mode)
    if (!userId) return
    try {
      await upsertMutation.mutateAsync({
        userId,
        emailEnabled: DEFAULT_NOTIFICATION_PREFERENCES.email_enabled,
        smsEnabled: DEFAULT_NOTIFICATION_PREFERENCES.sms_enabled,
        inAppEnabled: DEFAULT_NOTIFICATION_PREFERENCES.in_app_enabled,
        digestMode: DEFAULT_NOTIFICATION_PREFERENCES.digest_mode,
      })
      toast({
        title: 'Reset to defaults',
        description: 'Notification preferences have been reset to defaults.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to reset notifications',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  // Check if current settings differ from defaults
  const hasChanges =
    emailNotifications !== DEFAULT_NOTIFICATION_PREFERENCES.email_enabled ||
    smsNotifications !== DEFAULT_NOTIFICATION_PREFERENCES.sms_enabled ||
    digestMode !== DEFAULT_NOTIFICATION_PREFERENCES.digest_mode

  const isSaving = upsertMutation.isPending

  return (
    <div className="bg-card rounded-lg border p-6 space-y-6">
      <h2 className="text-lg font-semibold">Notifications</h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Email Notifications</p>
            <p className="text-sm text-muted-foreground">
              Receive email when new photos are uploaded
            </p>
          </div>
          <input
            type="checkbox"
            checked={emailNotifications}
            onChange={(e) => setEmailNotifications(e.target.checked)}
            className="h-5 w-5"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">SMS Notifications</p>
            <p className="text-sm text-muted-foreground">
              Receive SMS alerts for important updates
            </p>
          </div>
          <input
            type="checkbox"
            checked={smsNotifications}
            onChange={(e) => setSmsNotifications(e.target.checked)}
            className="h-5 w-5"
          />
        </div>

        {/* Digest Mode */}
        <div className="pt-4 border-t">
          <div className="mb-3">
            <p className="font-medium">Email Digest Mode</p>
            <p className="text-sm text-muted-foreground">
              Choose how often you want to receive email notifications
            </p>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="digestMode"
                value="immediate"
                checked={digestMode === 'immediate'}
                onChange={() => setDigestMode('immediate')}
                className="h-4 w-4 text-primary"
              />
              <div>
                <p className="font-medium">Instant</p>
                <p className="text-sm text-muted-foreground">Receive emails immediately for each notification</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="digestMode"
                value="daily"
                checked={digestMode === 'daily'}
                onChange={() => setDigestMode('daily')}
                className="h-4 w-4 text-primary"
              />
              <div>
                <p className="font-medium">Daily Digest</p>
                <p className="text-sm text-muted-foreground">Receive a daily summary email at 9:00 AM</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="digestMode"
                value="off"
                checked={digestMode === 'off'}
                onChange={() => setDigestMode('off')}
                className="h-4 w-4 text-primary"
              />
              <div>
                <p className="font-medium">No Email Digests</p>
                <p className="text-sm text-muted-foreground">Email digests off — in-app notifications still appear</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Notifications'}
        </Button>
        {hasChanges && (
          <Button variant="outline" onClick={handleResetToDefaults}>
            Reset to Defaults
          </Button>
        )}
      </div>
    </div>
  )
}

function PrivacySection() {
  const { user: _user, organization: _organization } = useAuth()
  const { toast: _toast } = useToast()
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  return (
    <>
      <div className="bg-card rounded-lg border p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Privacy</h2>
        </div>

        <p className="text-sm text-muted-foreground">
          Manage your data and privacy preferences. Under GDPR and other privacy regulations,
          you have the right to access and export your personal data.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="font-medium">Export My Data</p>
              <p className="text-sm text-muted-foreground">
                Download a copy of all your personal data including profile, organization, and drops
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsExportModalOpen(true)}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="font-medium">Data Retention</p>
              <p className="text-sm text-muted-foreground">
                Your data is retained as long as your account is active. Deleted data is removed within 30 days.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isExportModalOpen && (
        <ExportDataModal onClose={() => setIsExportModalOpen(false)} />
      )}
    </>
  )
}

function ExportDataModal({ onClose }: { onClose: () => void }) {
  const { user, organization, session } = useAuth()
  const { toast } = useToast()
  const dropsQuery = useDropsQuery()
  const [exportReady, setExportReady] = useState(false)
  const [exportData, setExportData] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Pull live drops from Supabase (org-scoped by row-level permissions). The
  // useDropsQuery hook is already mounted because this modal is rendered, so
  // the data is typically ready by the time the user clicks Generate.
  const generateExport = async () => {
    setIsExporting(true)
    try {
      const drops = dropsQuery.drops
      if (dropsQuery.isLoading) {
        await dropsQuery.refetch()
      }

      const data = {
        exportDate: new Date().toISOString(),
        exportFormat: 'GDPR Data Export v1.1',

        profile: {
          id: user?.id,
          name: user?.name,
          email: user?.email,
          avatarUrl: user?.avatarUrl ? '[Image URL]' : null,
        },

        organization: {
          id: organization?.id,
          name: organization?.name,
          tier: organization?.tier,
          role: organization?.role,
          pendingDowngrade: organization?.pendingDowngrade,
        },

        session: {
          createdAt: session?.createdAt ? new Date(session.createdAt).toISOString() : null,
          expiresAt: session?.expiresAt ? new Date(session.expiresAt).toISOString() : null,
        },

        drops: drops.map((drop) => ({
          id: drop.id,
          name: drop.name,
          description: drop.description ?? null,
          status: drop.status,
          isTrial: drop.isTrial,
          photoCount: drop.photoCount,
          contributorCount: drop.contributorCount,
          viewCount: drop.viewCount,
          createdAt: drop.createdAt.toISOString(),
          opensAt: drop.opensAt.toISOString(),
          closesAt: drop.closesAt.toISOString(),
          retentionExpiresAt: drop.retentionExpiresAt?.toISOString() ?? null,
          defaultContributorToken: drop.defaultContributorToken ?? null,
        })),

        summary: {
          totalDrops: drops.length,
          totalPhotos: drops.reduce((sum, d) => sum + d.photoCount, 0),
          totalViews: drops.reduce((sum, d) => sum + d.viewCount, 0),
          activeDrops: drops.filter((d) => d.status === 'active').length,
          closedDrops: drops.filter((d) => d.status === 'closed').length,
        },
      }

      setExportData(JSON.stringify(data, null, 2))
      setExportReady(true)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const downloadExport = () => {
    if (!exportData) return

    const blob = new Blob([exportData], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `photodrop-data-export-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast({
      title: 'Data exported',
      description: 'Your data has been downloaded successfully.',
    })

    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card rounded-lg shadow-lg max-w-lg w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <FileJson className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-semibold">Export Your Data</h2>
        </div>

        {!exportReady ? (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              This will generate a JSON file containing all your personal data stored in Photodrop, including:
            </p>

            <ul className="space-y-2 mb-6">
              <li className="flex items-center text-sm">
                <span className="w-2 h-2 bg-primary rounded-full mr-3" />
                Profile information (name, email)
              </li>
              <li className="flex items-center text-sm">
                <span className="w-2 h-2 bg-primary rounded-full mr-3" />
                Organization details
              </li>
              <li className="flex items-center text-sm">
                <span className="w-2 h-2 bg-primary rounded-full mr-3" />
                All your drops and their metadata
              </li>
              <li className="flex items-center text-sm">
                <span className="w-2 h-2 bg-primary rounded-full mr-3" />
                Account-wide summary (drop counts, photo counts, view totals)
              </li>
            </ul>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mb-6">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Photo files are not included in this export due to size constraints.
                Contact support if you need a full media export.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={generateExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileJson className="h-4 w-4" />
                    Generate Export
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-foreground font-medium mb-2">
                <svg className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Export Ready
              </div>
              <p className="text-sm text-muted-foreground">
                Your data export has been generated and is ready for download.
              </p>
            </div>

            {/* Preview */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Preview:</p>
              <div className="bg-muted rounded-lg p-3 max-h-40 overflow-auto">
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
                  {exportData?.substring(0, 500)}...
                </pre>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Close
              </Button>
              <Button className="flex-1 gap-2" onClick={downloadExport}>
                <Download className="h-4 w-4" />
                Download JSON
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function ChangeEmailModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth()
  const { user: clerkUser } = useUser()
  const { toast } = useToast()
  const [phase, setPhase] = useState<'email' | 'verify'>('email')
  const [newEmail, setNewEmail] = useState('')
  const [code, setCode] = useState('')
  const [pendingEmailAddressId, setPendingEmailAddressId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
  const isFormValid = isValidEmail && newEmail !== user?.email

  const handleRequestVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isValidEmail) {
      setError('Please enter a valid email address')
      return
    }

    if (newEmail === user?.email) {
      setError('New email must be different from current email')
      return
    }

    setIsSubmitting(true)

    try {
      if (DEMO_AUTH_ENABLED || !clerkUser) {
        // Demo mode: skip Clerk; pretend the verification was sent
        await new Promise(resolve => setTimeout(resolve, 500))
        toast({
          title: 'Verification email sent',
          description: `(Demo) A verification email would have been sent to ${newEmail}.`,
        })
        onClose()
        return
      }

      const created = await clerkUser.createEmailAddress({ email: newEmail })
      await clerkUser.reload()
      const newAddr = clerkUser.emailAddresses.find((a) => a.id === created.id)
      if (!newAddr) {
        setError('Failed to create email address. Please try again.')
        setIsSubmitting(false)
        return
      }
      await newAddr.prepareVerification({ strategy: 'email_code' })
      setPendingEmailAddressId(newAddr.id)
      setPhase('verify')
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to send verification code.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!code || !pendingEmailAddressId || !clerkUser) {
      setError('Verification code is required')
      return
    }

    setIsSubmitting(true)

    try {
      const addr = clerkUser.emailAddresses.find((a) => a.id === pendingEmailAddressId)
      if (!addr) {
        setError('Pending email address not found. Please retry.')
        setIsSubmitting(false)
        return
      }
      const result = await addr.attemptVerification({ code })
      if (result.verification.status !== 'verified') {
        setError('Verification failed. Please try again.')
        setIsSubmitting(false)
        return
      }
      await clerkUser.update({ primaryEmailAddressId: addr.id })

      // Best-effort sync to the app user row — non-blocking on failure.
      try {
        await syncCurrentUserEmail(newEmail)
      } catch (err) {
        console.error('Failed to sync email to database:', err)
      }

      toast({ title: 'Email updated', description: 'Your email address has been changed.' })
      onClose()
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to verify code.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (!clerkUser || !pendingEmailAddressId) return
    setError('')
    try {
      const addr = clerkUser.emailAddresses.find((a) => a.id === pendingEmailAddressId)
      await addr?.prepareVerification({ strategy: 'email_code' })
      toast({ title: 'Code resent', description: `A new code was sent to ${newEmail}.` })
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to resend code.'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-card rounded-lg shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <Mail className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-semibold">Change Email</h2>
        </div>

        {phase === 'email' ? (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Enter your new email address. A verification code will be sent to confirm the change.
            </p>

            <form onSubmit={handleRequestVerification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-muted-foreground">
                  Current Email
                </label>
                <div className="px-3 py-2 border rounded-md bg-muted/50 text-muted-foreground text-sm">
                  {user?.email}
                </div>
              </div>

              <div>
                <label htmlFor="new-email" className="block text-sm font-medium mb-2">
                  New Email Address
                </label>
                <input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="newemail@example.com"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="email"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={!isFormValid || isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Verification'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Enter the 6-digit code sent to <strong>{newEmail}</strong>.
            </p>

            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label htmlFor="verify-code" className="block text-sm font-medium mb-2">
                  Verification Code
                </label>
                <input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono tracking-widest text-center text-lg"
                  autoComplete="one-time-code"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={code.length !== 6 || isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
              </div>

              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm text-primary hover:underline w-full text-center mt-2"
                disabled={isSubmitting}
              >
                Resend code
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

function DangerZone() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <div className="bg-card rounded-lg border border-destructive/50 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-destructive">Danger Zone</h2>
        <p className="text-sm text-muted-foreground">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <Button
          variant="destructive"
          onClick={() => setIsModalOpen(true)}
        >
          Delete Account
        </Button>
      </div>

      {isModalOpen && (
        <DeleteAccountModal onClose={() => setIsModalOpen(false)} />
      )}
    </>
  )
}

function DeleteAccountModal({ onClose }: { onClose: () => void }) {
  const { signOut } = useAuth()
  const { user: clerkUser } = useUser()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [confirmText, setConfirmText] = useState('')
  const [showSecondConfirm, setShowSecondConfirm] = useState(false)
  const [error, setError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const isConfirmTextValid = confirmText === 'DELETE'

  const handleSubmitFirstStage = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!isConfirmTextValid) {
      setError('Please type DELETE to confirm')
      return
    }
    setShowSecondConfirm(true)
  }

  const handleConfirmDelete = async () => {
    setError('')
    setIsDeleting(true)

    try {
      if (DEMO_AUTH_ENABLED || !clerkUser) {
        // Demo mode: clear session and redirect
        await new Promise((r) => setTimeout(r, 500))
        await signOut()
        toast({ title: 'Account deleted', description: '(Demo) Your account has been deleted.' })
        navigate('/login', { replace: true })
        return
      }

      // Real Clerk delete — follow-up cleanup removes the user's application rows.
      await clerkUser.delete()
      toast({ title: 'Account deleted', description: 'Your account has been permanently deleted.' })
      navigate('/login', { replace: true })
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to delete account. Please try again.'))
      setIsDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={isDeleting ? undefined : onClose} />

      <div className="relative bg-card rounded-lg shadow-lg max-w-md w-full mx-4 p-6 z-10">
        {!isDeleting && (
          <button
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="flex items-center gap-3 text-destructive mb-4">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Delete Account</h2>
        </div>

        {!showSecondConfirm ? (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              This action cannot be undone. This will permanently delete your account
              and remove all of your data including all drops, photos, and settings.
            </p>

            <form onSubmit={handleSubmitFirstStage} className="space-y-4">
              <div>
                <label htmlFor="confirm-delete" className="block text-sm font-medium mb-2">
                  Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm
                </label>
                <input
                  id="confirm-delete"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-destructive font-mono"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="destructive" className="flex-1" disabled={!isConfirmTextValid}>
                  Continue
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p className="text-sm font-medium mb-2">Are you absolutely sure?</p>
            <p className="text-sm text-muted-foreground mb-6">
              This will sign you out and permanently delete your account. You will not be able to recover it.
            </p>

            {error && <p className="text-sm text-destructive mb-4">{error}</p>}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowSecondConfirm(false)}
                disabled={isDeleting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete my account'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
